import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { INITIAL_STATE, reducer, type Action, type AppState } from './state';
import { load, save } from './persist';
import { today } from '../engine/dates';
import type { TopicId } from '../content/types';

interface Store {
  readonly state: AppState;
  readonly dispatch: (action: Action) => void;
  /** False until the saved state has been read back, to avoid a zero-flash. */
  readonly ready: boolean;
}

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [ready, setReady] = useState(false);
  const hydrated = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void load().then((saved) => {
      if (cancelled) return;
      if (Object.keys(saved).length > 0) dispatch({ type: 'hydrate', state: saved });
      // Settle any days missed while the app was closed, so the streak she
      // sees on open is the streak she actually has — not a stale number that
      // silently changes the moment she finishes a set.
      dispatch({ type: 'settleDay', today: today() });
      hydrated.current = true;
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Auto-save after every interaction. Putting the phone down mid-question is
   * the normal case in a shop, not the exception — closing the app must never
   * cost her anything.
   *
   * The hydrated guard matters: without it the empty initial state would be
   * written over real saved progress in the moment before load() resolves.
   */
  useEffect(() => {
    if (!hydrated.current) return;
    void save(state);
  }, [state]);

  // Settings that the stylesheet reads off the document element.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.font = state.settings.font;
    root.dataset.motion = state.settings.reduceMotion ? 'off' : 'on';
  }, [state.settings.font, state.settings.reduceMotion]);

  return (
    <StoreContext.Provider value={{ state, dispatch, ready }}>{children}</StoreContext.Provider>
  );
}

export function useApp(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useApp must be used inside a StoreProvider');
  return store;
}

/**
 * Start a set. Centralised so the impure bits — the clock, which supplies both
 * the seed and the practice date — live in exactly one place and the reducer
 * stays a pure function of its inputs.
 */
export function useStartSet(): (topicId: TopicId) => void {
  const { dispatch } = useApp();
  return (topicId) =>
    dispatch({
      type: 'start',
      topicId,
      seed: `${topicId}:${Date.now()}`,
      today: today(),
    });
}

/**
 * A short buzz on a correct answer. Deliberately tiny — this is a nod, not a
 * fanfare — and silenced by both the haptics setting and reduce-motion.
 */
export function useHaptics(): (kind: 'right' | 'wrong') => void {
  const { state } = useApp();
  return (kind) => {
    if (!state.settings.haptics || state.settings.reduceMotion) return;
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
    navigator.vibrate(kind === 'right' ? 12 : [8, 40, 8]);
  };
}
